import { useEffect, useState } from "react";
import { pixelTheme } from "../theme/pixel";
import {
  getMusicVolume,
  isMusicMuted,
  setMusicMuted,
  setMusicVolume,
  subscribeMusic,
} from "../logic/battleAudio";
import {
  getSfxVolume,
  isSfxMuted,
  playSfx,
  setSfxMuted,
  setSfxVolume,
  subscribeSfx,
} from "../logic/sfx";
import {
  ANIM_SPEED_BOUNDS,
  getAnimSpeed,
  setAnimSpeed,
  subscribeAnimSpeed,
} from "../logic/animSpeed";
import { useAdminMode } from "../logic/useAdminMode";

// Two volume sliders (music + sfx). Mute is distinct from dragging to
// 0: the underlying volume is preserved across mute toggles.
export function VolumePanel({ mode = "horizontal" }: { mode?: "horizontal" | "stacked" }) {
  const [, force] = useState(0);
  useEffect(() => {
    const tick = () => force((n) => n + 1);
    const u1 = subscribeMusic(tick);
    const u2 = subscribeSfx(tick);
    const u3 = subscribeAnimSpeed(tick);
    return () => {
      u1();
      u2();
      u3();
    };
  }, []);
  const isAdminMode = useAdminMode();

  const musicMuted = isMusicMuted();
  const sfxMuted = isSfxMuted();
  const musicVol = getMusicVolume();
  const sfxVol = getSfxVolume();

  const wrap: React.CSSProperties =
    mode === "stacked"
      ? { display: "flex", flexDirection: "column", gap: 4 }
      : { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" };

  return (
    <div style={wrap}>
      <VolumeRow
        label="MUSIC"
        muted={musicMuted}
        value={musicVol}
        onToggleMute={() => setMusicMuted(!musicMuted)}
        onChange={setMusicVolume}
      />
      <VolumeRow
        label="SFX"
        muted={sfxMuted}
        value={sfxVol}
        onToggleMute={() => {
          const next = !sfxMuted;
          setSfxMuted(next);
          if (!next) playSfx("ai_button_click");
        }}
        onChange={(v) => {
          setSfxVolume(v);
        }}
      />
      {isAdminMode && <AnimSpeedRow />}
    </div>
  );
}

// Admin-only slider for the global animation-speed multiplier. Drives
// `--doi-anim-speed`; lets a user crank the speed down to inspect each
// phase of a transition by eye (0.025x is ~40x slowdown).
function AnimSpeedRow() {
  const theme = pixelTheme;
  const speed = getAnimSpeed();
  const pct = Math.round(speed * 1000);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px",
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
      }}
      title={`Animation speed ${speed.toFixed(3)}x (admin)`}
    >
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 8,
          letterSpacing: theme.letterSpacing,
          color: theme.inkDim,
          minWidth: 32,
        }}
      >
        ANIM
      </span>
      <input
        // Slider works in thousandths so we can reach the 0.025x floor
        // without a fractional `step`. UI label still shows the natural
        // multiplier (0.025x .. 2.000x).
        type="range"
        min={Math.round(ANIM_SPEED_BOUNDS.min * 1000)}
        max={Math.round(ANIM_SPEED_BOUNDS.max * 1000)}
        step={25}
        value={pct}
        onChange={(e) => setAnimSpeed(Number(e.target.value) / 1000)}
        aria-label="Animation speed"
        style={{ width: 80, accentColor: theme.accent, cursor: "pointer" }}
      />
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 8,
          color: theme.inkDim,
          minWidth: 40,
          textAlign: "right",
        }}
      >
        {speed.toFixed(3)}x
      </span>
    </div>
  );
}

interface VolumeRowProps {
  label: string;
  muted: boolean;
  value: number;
  onToggleMute: () => void;
  onChange: (v: number) => void;
}

function VolumeRow({
  label,
  muted,
  value,
  onToggleMute,
  onChange,
}: VolumeRowProps) {
  const theme = pixelTheme;
  const displayVal = muted ? 0 : value;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 8px",
        background: theme.panel,
        border: `2px solid ${theme.panelEdge}`,
      }}
      title={`${label} ${muted ? "muted" : `${value}%`}`}
    >
      <button
        onClick={onToggleMute}
        aria-label={`Toggle ${label.toLowerCase()} mute`}
        title={muted ? `${label} muted — click to unmute` : `${label} on — click to mute`}
        style={{
          background: "transparent",
          border: "none",
          padding: 0,
          margin: 0,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        <SpeakerIcon muted={muted} ink={theme.ink} dim={theme.inkFaint} />
      </button>
      <span
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 8,
          letterSpacing: theme.letterSpacing,
          color: theme.inkDim,
          minWidth: 32,
        }}
      >
        {label}
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={displayVal}
        disabled={muted}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={`${label} volume`}
        style={{ width: 80, accentColor: theme.accent, cursor: muted ? "not-allowed" : "pointer" }}
      />
    </div>
  );
}

function SpeakerIcon({
  muted,
  ink,
  dim,
}: {
  muted: boolean;
  ink: string;
  dim: string;
}) {
  const SPEAKER: number[][] = [
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  ];
  const WAVES: number[][] = [
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  ];
  const SLASH: number[][] = [
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
    [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  ];
  const cell = 2;
  const size = cell * 16;
  const rects: React.ReactElement[] = [];
  for (let y = 0; y < 16; y++) {
    for (let x = 0; x < 16; x++) {
      if (SPEAKER[y][x]) {
        rects.push(
          <rect
            key={`s-${x}-${y}`}
            x={x * cell}
            y={y * cell}
            width={cell}
            height={cell}
            fill={ink}
          />,
        );
      }
      if (!muted && WAVES[y][x]) {
        rects.push(
          <rect
            key={`w-${x}-${y}`}
            x={x * cell}
            y={y * cell}
            width={cell}
            height={cell}
            fill={dim}
          />,
        );
      }
      if (muted && SLASH[y][x]) {
        rects.push(
          <rect
            key={`m-${x}-${y}`}
            x={x * cell}
            y={y * cell}
            width={cell}
            height={cell}
            fill="#a22633"
          />,
        );
      }
    }
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      {rects}
    </svg>
  );
}
